import React, { useState, useMemo } from 'react';
import TimesheetGrid from './TimesheetGrid';
import { PAY_TYPES, money, num, plural } from './tsFormat';

/* Коды ввода — подсказка над сеткой. Показаны так же, как выглядят в клетках. */
const LEGEND = [
  { code: '12', type: 'work', text: 'часы' },
  { code: '1', type: 'work', text: 'смена или день оклада' },
  { code: 'в', type: 'dayoff', text: 'выходной' },
  { code: 'отп', type: 'vacation', text: 'отпуск' },
  { code: 'б', type: 'sick', text: 'больничный' },
  { code: 'п/р', type: 'idle', text: 'простой' },
  { code: 'ст', type: 'training', text: 'стажировка' },
  { code: 'ув', type: 'off', text: 'не в штате' },
];

const AddEntryModal = ({ employees, entries, jobPositions, onClose, onAdd, onSync }) => {
  const missing = employees.filter(e => e.status === 'active' && !entries.some(x => x.employee_id === e.id));
  const sorted = useMemo(() => [...employees].sort((a, b) => (
    (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1) || a.full_name.localeCompare(b.full_name, 'ru')
  )), [employees]);
  const [form, setForm] = useState({ employee_id: '', position: '', pay_type: 'hourly', rate: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const pickEmployee = (id) => {
    const emp = employees.find(e => String(e.id) === String(id));
    setForm({
      employee_id: id,
      position: emp?.position || '',
      pay_type: emp?.pay_type === 'percent' ? 'hourly' : (emp?.pay_type || 'hourly'),
      rate: emp?.rate || '',
    });
  };

  const pickPosition = (name) => {
    const known = jobPositions.find(p => p.name === name);
    setForm(f => ({
      ...f,
      position: name,
      ...(known && known.default_rate ? { pay_type: known.default_pay_type, rate: known.default_rate } : {}),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) { setError('Выберите сотрудника'); return; }
    setBusy(true);
    setError(null);
    const err = await onAdd({ ...form, employee_id: Number(form.employee_id), rate: Number(form.rate) || 0 });
    setBusy(false);
    if (err) setError(err); else onClose();
  };

  const payType = PAY_TYPES.find(p => p.value === form.pay_type) || PAY_TYPES[0];

  return (
    <div className="ui-modal" onClick={onClose}>
      <div className="ui-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="ui-modal-head">
          <h2>Добавить в табель</h2>
          <button className="ui-icon-btn" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        {missing.length > 0 && (
          <div className="ts-add-missing">
            <span>
              {missing.length} {plural(missing.length, 'работающий сотрудник', 'работающих сотрудника', 'работающих сотрудников')} ещё
              не в этом месяце: {missing.slice(0, 3).map(m => m.full_name).join(', ')}{missing.length > 3 ? '…' : ''}
            </span>
            <button type="button" className="ui-btn-ghost" onClick={async () => { await onSync(); onClose(); }}>
              Добавить всех
            </button>
          </div>
        )}

        <form className="ui-form" onSubmit={submit}>
          <p className="ui-dim ts-add-hint">
            Человек может стоять в месяце дважды на разных должностях со своей ставкой — например,
            бармен, который подменял официантов.
          </p>
          <div className="ui-grid">
            <div className="ui-field wide">
              <label htmlFor="ts-add-emp">Сотрудник</label>
              <select id="ts-add-emp" value={form.employee_id} onChange={(e) => pickEmployee(e.target.value)}>
                <option value="">— выберите —</option>
                {sorted.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name}{emp.status === 'fired' ? ' (уволен)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="ui-field">
              <label htmlFor="ts-add-pos">Должность в этом месяце</label>
              <input id="ts-add-pos" list="ts-positions" value={form.position}
                onChange={(e) => pickPosition(e.target.value)} placeholder="Официант" />
              <datalist id="ts-positions">
                {jobPositions.map(p => <option key={p.id} value={p.name} />)}
              </datalist>
            </div>
            <div className="ui-field">
              <label htmlFor="ts-add-type">Оплата</label>
              <select id="ts-add-type" value={form.pay_type}
                onChange={(e) => setForm(f => ({ ...f, pay_type: e.target.value }))}>
                {PAY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="ui-field">
              <label htmlFor="ts-add-rate">{payType.rateLabel}</label>
              <input id="ts-add-rate" type="number" min="0" step="1" value={form.rate}
                onChange={(e) => setForm(f => ({ ...f, rate: e.target.value }))} />
            </div>
          </div>
          {error && <div className="ui-inline-error">{error}</div>}
          <div className="ui-modal-actions">
            <button type="button" className="ui-btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="ui-btn-primary" disabled={busy}>
              {busy ? 'Добавляю…' : 'Добавить строку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TimesheetTab = ({
  data, closed, todayDay, employees, jobPositions,
  onSaveCells, onSaveEntry, onResetHalf, onOpenEntry, onClosedDay, onAddEntry, onSync,
}) => {
  const [filter, setFilter] = useState({ search: '', group: 'all' });
  const [adding, setAdding] = useState(false);
  const { period, entries, summary, days, days_in_month: daysInMonth } = data;

  const groups = useMemo(() => [...new Set(entries.map(e => e.position))], [entries]);

  return (
    <div className="ts-tab">
      <div className="ts-ledger" aria-label="Итоги месяца">
        <div className="ts-ledger-main">
          <span className="ts-ledger-label">ФОТ за {period.label.toLowerCase()}</span>
          <span className="ts-ledger-value">{money(summary.total)}</span>
        </div>
        <dl className="ts-ledger-parts">
          <div><dt>За 1–15</dt><dd>{money(summary.half1)}</dd></div>
          <div><dt>За 16–{daysInMonth}</dt><dd>{money(summary.half2)}</dd></div>
          <div><dt>Доплаты</dt><dd>{money(summary.bonus)}</dd></div>
          <div><dt>Штрафы</dt><dd className={summary.fines ? 'is-neg' : ''}>{summary.fines ? `−${money(summary.fines)}` : money(0)}</dd></div>
          <div><dt>Людей</dt><dd>{summary.count}</dd></div>
          <div><dt>Часов</dt><dd>{num(summary.hours)}</dd></div>
          {summary.shifts > 0 && <div><dt>Смен</dt><dd>{num(summary.shifts)}</dd></div>}
        </dl>
      </div>

      <div className="ts-toolbar">
        <input
          className="ts-search"
          type="search"
          placeholder="Найти сотрудника"
          aria-label="Найти сотрудника"
          value={filter.search}
          onChange={(e) => setFilter(f => ({ ...f, search: e.target.value }))}
        />
        <select
          className="ts-select"
          aria-label="Должность"
          value={filter.group}
          onChange={(e) => setFilter(f => ({ ...f, group: e.target.value }))}
        >
          <option value="all">Все должности</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <ul className="ts-legend" aria-label="Что вводить в клетку">
          {LEGEND.map(item => (
            <li key={item.code + item.text}>
              <span className={`ts-legend-code t-${item.type}`}>{item.code}</span>
              {item.text}
            </li>
          ))}
        </ul>
        {!closed && (
          <button type="button" className="ui-btn-ghost ts-add-btn" onClick={() => setAdding(true)}>
            Добавить в табель
          </button>
        )}
      </div>

      <TimesheetGrid
        period={period}
        entries={entries}
        days={days}
        daysInMonth={daysInMonth}
        closed={closed}
        todayDay={todayDay}
        filter={filter}
        onSaveCells={onSaveCells}
        onSaveEntry={onSaveEntry}
        onResetHalf={onResetHalf}
        onOpenEntry={onOpenEntry}
        onClosedDay={onClosedDay}
      />

      {adding && (
        <AddEntryModal
          employees={employees}
          entries={entries}
          jobPositions={jobPositions}
          onClose={() => setAdding(false)}
          onAdd={onAddEntry}
          onSync={onSync}
        />
      )}
    </div>
  );
};

export default TimesheetTab;
